import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Typography,
  Input,
  Select,
  Popconfirm,
  Switch,
  Tooltip,
  Row,
  Col,
  Breadcrumb,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  AppstoreOutlined,
  SearchOutlined,
  FilterOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import authApi from '../services/authApi';
import EstimateTemplateForm from '../components/Settings/EstimateTemplateForm';

const { Title, Text } = Typography;
const { Search } = Input;

interface EstimateTemplate {
  id: number;
  template_name: string;
  category: string;
  description?: string;
  is_public: boolean;
  is_active: boolean;
  total_amount: number;
  estimated_duration_hours: number;
  tags?: string[];
  created_at: string;
  creator?: {
    first_name: string;
    last_name: string;
  };
}

const EstimateTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState<EstimateTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [formVisible, setFormVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EstimateTemplate | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  // Determine the role prefix from current path
  const getRolePrefix = () => {
    if (location.pathname.startsWith('/owner')) return '/owner';
    if (location.pathname.startsWith('/advisor')) return '/advisor';
    return '/admin';
  };

  const rolePrefix = getRolePrefix();

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, [categoryFilter, statusFilter]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (searchText) params.search = searchText;
      if (categoryFilter) params.category = categoryFilter;
      if (statusFilter === 'active') params.is_active = true;
      if (statusFilter === 'inactive') params.is_active = false;

      const response = await authApi.get('/admin/estimate-templates', { params });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Failed to load estimate templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('http://localhost:3001/api/public/estimate-templates/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setFormVisible(true);
  };

  const handleEditTemplate = async (id: number) => {
    try {
      const response = await authApi.get(`/admin/estimate-templates/${id}`);
      setEditingTemplate(response.data);
      setFormVisible(true);
    } catch (error) {
      console.error('Error fetching template:', error);
      message.error('Failed to load template details');
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      await authApi.patch(`/admin/estimate-templates/${id}/toggle-active`, {});
      message.success(`Template ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchTemplates();
    } catch (error) {
      console.error('Error toggling template status:', error);
      message.error('Failed to update template status');
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    try {
      await authApi.delete(`/admin/estimate-templates/${id}`);
      message.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error('Failed to delete template');
    }
  };

  const handleFormSuccess = () => {
    setFormVisible(false);
    setEditingTemplate(null);
    fetchTemplates();
  };

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'template_name',
      key: 'template_name',
      width: 200,
      fixed: 'left' as const,
      render: (text: string, record: EstimateTemplate) => (
        <div>
          <Text strong>{text}</Text>
          {record.description && (
            <div>
              <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                {record.description}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 100,
      render: (amount: number) => (
        <Text strong style={{ color: '#52c41a' }}>
          ${amount.toFixed(2)}
        </Text>
      ),
      sorter: (a: EstimateTemplate, b: EstimateTemplate) => a.total_amount - b.total_amount,
    },
    {
      title: 'Duration',
      dataIndex: 'estimated_duration_hours',
      key: 'estimated_duration_hours',
      width: 100,
      render: (hours: number) => `${hours} hrs`,
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_: any, record: EstimateTemplate) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.is_active ? 'success' : 'default'}>
            {record.is_active ? 'Active' : 'Inactive'}
          </Tag>
          <Tag color={record.is_public ? 'processing' : 'default'} style={{ fontSize: 10 }}>
            {record.is_public ? 'Public' : 'Private'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags: string[]) => (
        <>
          {tags && tags.length > 0 ? (
            tags.slice(0, 2).map((tag, idx) => (
              <Tag key={idx} style={{ marginBottom: 4 }}>
                {tag}
              </Tag>
            ))
          ) : (
            <Text type="secondary">-</Text>
          )}
        </>
      ),
    },
    {
      title: 'Created By',
      key: 'creator',
      width: 120,
      render: (_: any, record: EstimateTemplate) => (
        record.creator ? (
          <Text>{record.creator.first_name} {record.creator.last_name}</Text>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: EstimateTemplate) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditTemplate(record.id)}
            />
          </Tooltip>
          <Tooltip title={record.is_active ? 'Deactivate' : 'Activate'}>
            <Switch
              size="small"
              checked={record.is_active}
              onChange={() => handleToggleActive(record.id, record.is_active)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this template?"
            onConfirm={() => handleDeleteTemplate(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '100%' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header with Breadcrumb */}
        <div>
          <Breadcrumb
            items={[
              {
                href: `${rolePrefix}/dashboard`,
                title: <HomeOutlined />,
              },
              {
                title: 'Estimate Templates',
              },
            ]}
            style={{ marginBottom: 16 }}
          />
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Space direction="vertical" size={0}>
                <Title level={2} style={{ margin: 0 }}>
                  <AppstoreOutlined /> Estimate Templates
                </Title>
                <Text type="secondary">
                  Create and manage pre-built service packages for customer portal
                </Text>
              </Space>
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateNew}
                size="large"
              >
                Create Template
              </Button>
            </Col>
          </Row>
        </div>

        {/* Filters Card */}
        <Card>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder="Search templates..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={fetchTemplates}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Select
                placeholder="Category"
                allowClear
                style={{ width: '100%' }}
                value={categoryFilter}
                onChange={setCategoryFilter}
              >
                {categories.map(cat => (
                  <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={setStatusFilter}
              >
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="inactive">Inactive</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={24} md={8} style={{ textAlign: 'right' }}>
              <Space>
                <Text type="secondary">{templates.length} templates</Text>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => {
                    setSearchText('');
                    setCategoryFilter(undefined);
                    setStatusFilter(undefined);
                  }}
                >
                  Clear Filters
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Templates Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={templates}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} templates`,
              responsive: true,
            }}
            scroll={{ x: 1200 }}
            size="middle"
          />
        </Card>
      </Space>

      {/* Template Form Modal */}
      <EstimateTemplateForm
        visible={formVisible}
        template={editingTemplate}
        onClose={() => {
          setFormVisible(false);
          setEditingTemplate(null);
        }}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};

export default EstimateTemplatesPage;
